// BondCharacteristicTab.jsx
import React, { useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { bondTypes, fieldOptions } from "./optionsHelper";
import { inputStyle } from "./styles";
import BondCharacteristicField from "./BondCharacteristicsFields";
import FilterDetailsBoxes from "./FilterDetailsBoxes";
import { preprocessFilters } from "../OrderComponents/columnConfigUtils";
import { fetchFilterInitialValues, fetchIssuers, fetchSnpRatingOptions, fetchMoodyRatingOptions, getSecurityChargeOptions } from "../../http/api";
import dayjs from "dayjs";
import { useAuth } from "../../Context/AuthProvider";
import TextareaAutosize from '@mui/material/TextareaAutosize';
import { jwtDecode } from 'jwt-decode';
import { IntegerInput, FloatInput } from './NumericInputs';
import { BOND_TYPES, ORDER_SIDES, USER_ROLES, ORDER_ENTRY_SECONDARY_SOURCES } from "../../Constants/AppConstants";
import { tokens } from "../../Common/designTokens"; // added

const textAreaStyle = {
  width: '100%',
  minHeight: '32px',
  padding: '8px',
  boxSizing: 'border-box',
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: '8px', // consistent 8px radius
  backgroundColor: tokens.colors.white,
  resize: 'vertical',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  overflowX: 'hidden',
  lineHeight: '32px',
  color: tokens.colors.fontColour,
};

const stickyBoxStyle = {
  position: "sticky",
  top: 0,
  backgroundColor: tokens.colors.bodyBackgroundColor,
  zIndex: 10,
};

const BondCharacteristicTab = ({
  accountIdentifiers: propAccountIdentifiers,
  readOnly = false,
  errors = {},
  onValidationChange,
  secondarySource,
}) => {
  const {
    bondCharacteristicData,
    setBondCharacteristicData,
    clearFieldError,
    checked,
    setChecked,
    removeFilter,
    accountIdentifiers: storeAccountIdentifiers,
    setBondCharacteristicErrors,
    validationErrors,
    clearValidationErrors,
    fetchAccounts,
  } = useBondCharacteristicStore();

  // Add issuerOptions state
  const [issuerOptions, setIssuerOptions] = React.useState([]);
  // Add Moody/S&P options state
  const [moodyRatingOptions, setMoodyRatingOptions] = React.useState([]);
  const [spRatingOptions, setSpRatingOptions] = React.useState([]);
  const [securityChargeOptions, setSecurityChargeOptions] = React.useState([]);

  // Add focus state for comments textarea
  const [isCommentsFocused, setIsCommentsFocused] = React.useState(false);

  const fields = bondTypes[bondCharacteristicData.bondType]?.fields || [];
  // Ensure priority fields appear first (when applicable)
  const priorityOrder = [
    "couponRange",
    "callable",
    "taxable",
    "incomeSource",
    "securityCharge",
    "price",
    "zeroCoupon",
    "state",
    "insured",
  ];
  const orderedFields = [
    ...priorityOrder.filter((f) => fields.includes(f)),
    ...fields.filter((f) => !priorityOrder.includes(f)),
  ];
  const { token: jwtToken } = useAuth();
  const decodedToken = jwtDecode(jwtToken);
  const tokenRole = decodedToken.role;

  // When opened from the staging blotter "Source" column, render the
  // characteristic filters as read-only chips instead of checkbox-wise fields.
  const viewAsChips =
    secondarySource === ORDER_ENTRY_SECONDARY_SOURCES.SOURCE_COLUMN_VIEW;
  const chipInput = React.useMemo(() => {
    if (!viewAsChips) return null;
    // type & side are already shown in the read-only header section above.
    const { type, side, ...rest } = preprocessFilters(bondCharacteristicData) || {};
    return rest;
  }, [viewAsChips, bondCharacteristicData]);

  const effectiveAccountIdentifiers = propAccountIdentifiers;



  useEffect(() => {
    const tabEnum = { 0: "Gov", 1: "Corp", 2: "Muni" };
    const tabValue = bondCharacteristicData.bondType === BOND_TYPES.TREASURY ? 0 : 
                     bondCharacteristicData.bondType === BOND_TYPES.CORPORATES ? 1 : 2;
    const tabKey = tabEnum[tabValue];
    const payload = { type: tabKey };
  
    const getFilterInitialValues = async (jwtToken, payload) => {
      const res = await fetchFilterInitialValues(jwtToken, payload);
      if (res) {

        const prefilledData = {
          couponMin: res.minCoupon ?? null,
          couponMax: res.maxCoupon ?? null,
          priceMin: res.minPrice ?? null,
          priceMax: res.maxPrice ?? null,
          yieldMin: res.minYtm ?? null,
          yieldMax: res.maxYtm ?? null,
          // maturityMin: res.minMaturity ? dayjs(res.minMaturity) : null,
          // maturityMax: res.maxMaturity ? dayjs(res.maxMaturity) : null,
        };
        setBondCharacteristicData(prefilledData);
        // Set Moody/S&P options if present
        setMoodyRatingOptions(res.moodyRatingMap || []);
        setSpRatingOptions(res.spRatingMap || []);
      }
    };
    //if not read only, fetch initial values
    if (!readOnly) {
      getFilterInitialValues(jwtToken, payload);
    }
  }, [bondCharacteristicData.bondType]);

  useEffect(() => {
    if (effectiveAccountIdentifiers?.length > 0 && !bondCharacteristicData.account) {
      setBondCharacteristicData({ ...bondCharacteristicData, account: effectiveAccountIdentifiers[0] });
    }
  }, [effectiveAccountIdentifiers]);

  const handleCheckboxChange = (panel, field) => (event) => {
    const isChecked = event.target.checked;
    setChecked(panel, field, isChecked);
    if (!isChecked) {
      removeFilter(field);
    } else if (field === 'securityCharge') {
      const current = bondCharacteristicData.securityCharge;
      if (!current || (Array.isArray(current) && current.length === 0)) {
        setBondCharacteristicData({ securityCharge: ['ALL'] });
      }
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let isValid = true;
    
    if (name === 'qty' && value > 10000000) isValid = false;
    
    if (isValid) {
      setBondCharacteristicData({ ...bondCharacteristicData, [name]: value });

      if (value !== undefined && value !== '') {
        clearFieldError(name);
      }
    }
  };

  const renderAccountField = () => {
    const baseProps = {
      fullWidth: true,
      variant: "filled",
      sx: inputStyle,
      InputProps: { disableUnderline: true }
    };

    const accountProps = {
      ...baseProps,
      error: errors?.account,
    };

    // Simplified - same for both trader and PM roles
    return (
      <Grid item xs={3}>
        <TextField
          {...accountProps}
          select={!readOnly && readOnly !== "edit"}
          value={bondCharacteristicData.account || ""}
          onChange={isFieldEditable('account') ? handleChange : undefined}
          name="account"
          label="Account"
          disabled={!isFieldEditable('account')}
        >
          {!readOnly && readOnly !== "edit" &&
            effectiveAccountIdentifiers.map((identifier) => (
              <MenuItem key={identifier} value={identifier}>
                {identifier}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
    );
  };


  // Clear validation errors when component mounts - only run once
  useEffect(() => {
    clearValidationErrors();
  }, []); // Remove clearValidationErrors from dependencies

  // Check for validation errors and report to parent - memoize the callback
  const handleValidationChange = useCallback((hasValidationErrors) => {
    if (onValidationChange) {
      onValidationChange(hasValidationErrors);
    }
  }, [onValidationChange]);

  useEffect(() => {
    const hasValidationErrors = Object.values(validationErrors).some(Boolean);
    handleValidationChange(hasValidationErrors);
  }, [validationErrors, handleValidationChange]);

  // When component mounts, reset the validation tracking - only run once
  useEffect(() => {
    // Reset validation tracking
    if (typeof window !== 'undefined') {
      window.bondValidationErrors = {};
    }
    
    // Initial validation state (no errors)
    if (onValidationChange) {
      onValidationChange(false);
    }
    
    // Clean up when component unmounts
    return () => {
      if (typeof window !== 'undefined') {
        delete window.bondValidationErrors;
      }
    };
  }, []); // Remove onValidationChange from dependencies

  // Handle validation state changes from field components - memoize
  const handleFieldValidationChange = useCallback((hasErrors) => {
    if (onValidationChange) {
      const storeHasErrors = Object.values(validationErrors).some(Boolean);
      const anyErrors = storeHasErrors || (typeof window !== 'undefined' && 
        window.bondValidationErrors ? 
        Object.values(window.bondValidationErrors).some(Boolean) : 
        hasErrors);
      
      onValidationChange(anyErrors);
    }
  }, [onValidationChange, validationErrors]);

  // Fetch issuers when component mounts
  useEffect(() => {
    const fetchIssuerOptions = async () => {
      try {
        const issuers = await fetchIssuers(jwtToken);
        setIssuerOptions(issuers || []);
      } catch (error) {
        console.error("Error fetching issuers:", error);
        setIssuerOptions([]);
      }
    };

    const fetchRatingOptions = async () => {
      try {
        const [snpRatings, moodyRatings] = await Promise.all([
          fetchSnpRatingOptions(jwtToken),
          fetchMoodyRatingOptions(jwtToken)
        ]);
        setSpRatingOptions(snpRatings || []);
        setMoodyRatingOptions(moodyRatings || []);
      } catch (error) {
        console.error("Error fetching rating options:", error);
        setSpRatingOptions([]);
        setMoodyRatingOptions([]);
      }
    };

    const fetchSecurityCharges = async () => {
      try {
        const charges = await getSecurityChargeOptions(jwtToken);
        setSecurityChargeOptions((charges || []).filter(
          (charge) => charge !== null && charge !== undefined && String(charge).trim() !== ""
        ));
      } catch (error) {
        console.error("Error fetching security charges:", error);
        setSecurityChargeOptions([]);
      }
    };

    fetchIssuerOptions();
    fetchRatingOptions();
    fetchSecurityCharges();
  }, [jwtToken]);

  // New helper: qty, bondType and comments editable in edit mode
  const isFieldEditable = (fieldName) => {
    if (readOnly !== "edit") return !readOnly;
    return fieldName === 'qty' || fieldName === 'bondType' || fieldName === 'comments';
  };

  // Handlers for comments focus to change border color when selected
  const handleCommentsFocus = () => setIsCommentsFocused(true);
  const handleCommentsBlur = () => setIsCommentsFocused(false);

  return (
    <Box sx={{ 
      height: "100%", 
      display: "flex", 
      flexDirection: "column",
      overflow: "hidden",
      position: "relative",
    }}>
      <Box sx={{
        ...stickyBoxStyle,
        flexShrink: 0,
      }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                fullWidth
                select
                name="side"
                value={bondCharacteristicData.side || ""}
                onChange={isFieldEditable('side') ? handleChange : () => {}}
                variant="filled"
                label="Side"
                sx={inputStyle}
                InputProps={{ disableUnderline: true }}
                disabled={!isFieldEditable('side')}
                error={errors?.side}
              >
                <MenuItem value={ORDER_SIDES.BUY}>{ORDER_SIDES.BUY}</MenuItem>
                <MenuItem value={ORDER_SIDES.SELL}>{ORDER_SIDES.SELL}</MenuItem>
              </TextField>
            </Grid>
            
            {renderAccountField()}

            <Grid item xs={3}>
              <IntegerInput
                name="qty"
                value={bondCharacteristicData.qty || ""}
                onChange={isFieldEditable('qty') ? handleChange : () => {}}
                placeholder="Enter quantity"
                label="Quantity"
                disabled={!isFieldEditable('qty')}
                error={errors?.qty}
                maxValue={10000000}
                sx={inputStyle}
              />
            </Grid>

            {/* Ladder roll-over: only present when this characteristic belongs to a rung */}
            {bondCharacteristicData.ladderName && (
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  name="ladderName"
                  value={bondCharacteristicData.ladderName}
                  variant="filled"
                  label="Ladder Name"
                  sx={inputStyle}
                  InputProps={{ disableUnderline: true, readOnly: true }}
                  disabled
                />
              </Grid>
            )}
          </Grid>

          {bondCharacteristicData.reason && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="reason"
                  value={bondCharacteristicData.reason}
                  variant="filled"
                  label="Reason"
                  sx={inputStyle}
                  // Single line, same height as the Comments box next to it. The reason is
                  // a short system-generated string; title exposes it in full if it is
                  // ever long enough to clip.
                  title={bondCharacteristicData.reason}
                  InputProps={{ disableUnderline: true, readOnly: true }}
                  disabled
                />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mt: 1, mb: 2, alignItems: "center" }}>
            <Grid item xs={3} sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 1 }}>
                Type
              </Typography>
              <Select
                fullWidth
                name="bondType"
                value={bondCharacteristicData.bondType || ""}
                onChange={isFieldEditable('bondType') ? handleChange : () => {}}
                displayEmpty
                disabled={!isFieldEditable('bondType')}
                error={errors?.bondType}
                sx={{ height: "40px", ...inputStyle }}
              >
                {fieldOptions.bondType.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={9}>
              <Box sx={{ position: "relative", width: "100%" }}>
                <TextareaAutosize
                  name="comments"
                  value={bondCharacteristicData.comments || ""}
                  onChange={(e) => {
                    if (e.target.value.length <= 200 && isFieldEditable('comments')) {
                      handleChange(e);
                    }
                  }}
                  onFocus={handleCommentsFocus}
                  onBlur={handleCommentsBlur}
                  placeholder="Add Comments"
                  disabled={!isFieldEditable('comments')}
                  minRows={1}
                  maxRows={5}
                  style={{
                    // merge base style and dynamic border color on focus
                    ...textAreaStyle,
                    outline: 'none', // remove default browser outline
                    border: `1px solid ${isCommentsFocused ? tokens.palette.primary : tokens.colors.bodyBorderColor50}`,
                    boxShadow: isCommentsFocused ? `0 0 0 3px ${tokens.palette.primaryA10 || tokens.palette.primaryA30}` : 'none',
                    WebkitBoxShadow: isCommentsFocused ? `0 0 0 3px ${tokens.palette.primaryA10 || tokens.palette.primaryA30}` : 'none',
                    ...(readOnly === true && {
                      opacity: 0.95,
                      backgroundColor: tokens.colors.defaultGridRowColor,
                      cursor: 'not-allowed',
                      color: tokens.colors.disabledFontColour, // use disabled token
                      border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                      boxShadow: 'none',
                      WebkitBoxShadow: 'none',
                    })
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 8,
                    color: (bondCharacteristicData.comments?.length || 0) > 200 ? 'error.main' : tokens.colors.disabledFontColour,
                    fontSize: '0.75rem',
                    pointerEvents: 'none'
                  }}
                >
                  {bondCharacteristicData.comments?.length || 0}/200
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{
        flex: 1,
        overflow: "auto",
        minHeight: 0,
        height: "100%",
      }}>
        {viewAsChips ? (
          <Box sx={{ p: 2, pt: 1 }}>
            <FilterDetailsBoxes input={chipInput} />
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            p: 2,
            pt: 1,
            minHeight: "min-content",
          }}>
            {orderedFields.map((field) => (
              <BondCharacteristicField
                key={field}
                panelName="bondCharacteristic"
                checked={checked}
                onCheckboxChange={handleCheckboxChange}
                field={field}
                subtab={bondCharacteristicData.bondType}
                readOnly={readOnly === true ? true : false} // editable in edit mode
                bondCharacteristicData={bondCharacteristicData}
                setBondCharacteristicData={setBondCharacteristicData}
                handleBondCharacteristicChange={handleChange}
                fieldOptions={fieldOptions}
                inputStyle={inputStyle}
                onValidationChange={handleFieldValidationChange}
                issuerOptions={issuerOptions}
                moodyRatingOptions={moodyRatingOptions}
                spRatingOptions={spRatingOptions}
                securityChargeOptions={securityChargeOptions}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BondCharacteristicTab;
